import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { useAccountStore } from '@/store/use-account-store';
import { Order, useOrdersStore } from '@/store/use-orders-store';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { cn } from '@/utils/cn';
import { abbrFromSym, imgFromSym } from '@/utils/token-symbol';
import { formatBigInt } from '@/utils/value-format';

import { getIsOrderOpposite } from '../../utils/helpers';

type Props = {
  orderId: string;
  skipSubscription?: boolean;
  skipLink?: boolean;
};

export default function Symbol({ orderId, skipSubscription, skipLink }: Props) {
  const { t } = useTranslation();

  const address = useAccountStore((state) => state.address);
  const order = useOrdersStore(
    useShallow((state) => {
      const { orders } = state.getOrders(address);
      const order = orders[orderId] as Order | undefined;
      if (!order) return;
      return {
        symbol: order.symbol,
        initMarginRatio: order.initMarginRatio,
        isBuy: order.isBuy,
        orderType: order.orderType,
        status: order.status,
        reduceOnly: order.reduceOnly,
      };
    }),
  );

  useEffect(() => {
    if (skipSubscription || !order?.symbol) return;
    const { increaseDependenciesForSymbol, decreaseDependenciesForSymbol } =
      useWebSocketStore.getState();
    increaseDependenciesForSymbol(order.symbol);
    return () => {
      decreaseDependenciesForSymbol(order.symbol);
    };
  }, [order?.symbol, skipSubscription]);

  const content = (
    <>
      <img
        src={imgFromSym(order?.symbol ?? '')}
        title={order?.symbol ?? 'Symbol'}
        className="size-8 rounded-full object-contain"
        alt="Symbol"
      />

      {order && order.symbol && (
        <div className="flex flex-col items-start">
          <div className="text-xl font-semibold">
            {abbrFromSym(order.symbol)}
          </div>
          {(() => {
            const isOpposite = getIsOrderOpposite(order);
            const isLong = isOpposite ? !order.isBuy : order.isBuy;
            const leverage = order.initMarginRatio
              ? formatBigInt(10n ** 6n / order.initMarginRatio, 2n, {
                  digits: 0,
                  round: 'ceil',
                  style: 'leverage',
                })
              : '';
            return (
              <div
                className={cn('text-base', isLong ? 'text-green' : 'text-red')}
              >
                {isOpposite
                  ? isLong
                    ? t('ordersHistoryTable.onLong', { leverage })
                    : t('ordersHistoryTable.onShort', { leverage })
                  : isLong
                    ? t('ordersHistoryTable.long', { leverage })
                    : t('ordersHistoryTable.short', { leverage })}
              </div>
            );
          })()}
        </div>
      )}
    </>
  );

  if (skipLink) {
    return <div className="flex items-center gap-3">{content}</div>;
  }

  return (
    <Link
      to={`/trade/${order?.symbol ?? ''}`}
      className="flex items-center gap-3"
    >
      {content}
    </Link>
  );
}

export const symbolAccessorFn = (id: string) => {
  const { address } = useAccountStore.getState();
  return useOrdersStore.getState().getOrders(address).orders[id].symbol;
};
